import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CreditCard,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { useState, type ReactElement, type ReactNode } from 'react'

import { Fonte, type Origem } from './components/fonte'
import { GraficoSerie } from './components/grafico-serie'
import { corDaSerie } from './paleta'

import type {
  Cac,
  EstagioForecast,
  Leads,
  PontoSerie,
  Relatorio,
  SemanaFechada,
  SerieHistorica,
} from './types'

import { cn } from '@/lib/cn'
import { dataCurta, inteiro, moedaCompacta, percentual } from '@/lib/formato'

const MES_ABREV = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

function rotuloMes(mes: string): string {
  return MES_ABREV[(Number(mes.slice(5, 7)) - 1 + 12) % 12]!
}

/**
 * Variação do último mês contra o anterior, para o selo de tendência. Recebe só
 * meses com receita (o mês corrente incompleto entra como zero e distorceria).
 */
function variacaoMensal(pontos: PontoSerie[]): number {
  const n = pontos.length
  if (n < 2) return 0
  return (pontos[n - 1]!.bookings - pontos[n - 2]!.bookings) / pontos[n - 2]!.bookings
}

function Cartao({
  titulo,
  origem,
  campo,
  children,
  className,
}: {
  titulo: string
  origem: Origem
  campo?: string
  children: ReactNode
  className?: string
}): ReactElement {
  return (
    <div
      className={cn('bg-card/70 border-primary/15 flex flex-col rounded-2xl border p-3', className)}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-2xl font-semibold">{titulo}</h3>
        <Fonte origem={origem} campo={campo} />
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  )
}

/** Tabela em escala de telão pra aba de executivos — texto menor: cabe a lista inteira. */
function TabelaExecutivos({
  colunas,
  linhas,
}: {
  colunas: { chave: string; rotulo: string; direita?: boolean }[]
  linhas: { chave: string; celulas: Record<string, string> }[]
}): ReactElement {
  return (
    <table className="w-full text-lg">
      <thead>
        <tr className="border-border border-b">
          {colunas.map((coluna) => (
            <th
              key={coluna.chave}
              className={cn(
                'text-muted-foreground px-2 py-1.5 text-sm font-semibold uppercase tracking-wide',
                coluna.direita ? 'text-right' : 'text-left',
              )}
            >
              {coluna.rotulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((linha) => (
          <tr key={linha.chave} className="border-border/60 border-b last:border-0">
            {colunas.map((coluna) => (
              <td
                key={coluna.chave}
                className={cn('px-2 py-1.5', coluna.direita && 'text-right tabular-nums')}
              >
                {linha.celulas[coluna.chave]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Delta({ valor, rotulo }: { valor: number; rotulo: string }): ReactElement {
  const positivo = valor >= 0
  return (
    <div className="mt-4 flex items-center gap-3 text-2xl">
      <span
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-full border',
          positivo ? 'border-success text-success' : 'border-destructive text-destructive',
        )}
      >
        {positivo ? (
          <ArrowUpRight className="h-6 w-6" aria-hidden />
        ) : (
          <ArrowDownRight className="h-6 w-6" aria-hidden />
        )}
      </span>
      <b className={positivo ? 'text-success' : 'text-destructive'}>
        {positivo ? '+' : ''}
        {percentual(valor, 0)}
      </b>
      <span className="text-muted-foreground">{rotulo}</span>
    </div>
  )
}

function NumeroGrande({ children }: { children: ReactNode }): ReactElement {
  return <p className="text-primary font-serif text-7xl font-normal leading-none">{children}</p>
}

function BarrasMensais({ pontos, meta }: { pontos: PontoSerie[]; meta: number }): ReactElement {
  if (pontos.length === 0) {
    return <p className="text-muted-foreground text-2xl">Sem série no período.</p>
  }
  const max = Math.max(...pontos.map((p) => p.bookings), meta, 1)
  return (
    <div className="relative flex h-[200px] items-end gap-2">
      {meta > 0 ? (
        <div
          className="border-primary/60 absolute inset-x-0 border-t border-dashed"
          style={{ bottom: `${(meta / max) * 82 + 14}%` }}
        >
          <span className="text-primary absolute -top-6 left-0 text-sm">
            Meta {moedaCompacta(meta)}
          </span>
        </div>
      ) : null}
      {pontos.map((p, i) => {
        const ultimo = i === pontos.length - 1
        return (
          <div key={p.mes} className="flex flex-1 flex-col items-center justify-end gap-1.5">
            <span className="text-muted-foreground text-sm tabular-nums">
              {moedaCompacta(p.bookings)}
            </span>
            <div
              className={cn('w-full rounded-t', ultimo ? 'bg-primary' : 'bg-muted')}
              style={{ height: `${(p.bookings / max) * 82}%` }}
            />
            <span className="text-muted-foreground text-sm">{rotuloMes(p.mes)}</span>
          </div>
        )
      })}
    </div>
  )
}

function Sparkline({ pontos }: { pontos: PontoSerie[] }): ReactElement {
  if (pontos.length < 2) {
    return <p className="text-muted-foreground text-2xl">Sem série no período.</p>
  }
  const valores = pontos.map((p) => p.bookings)
  const max = Math.max(...valores)
  const min = Math.min(...valores)
  const span = max - min || 1
  const coords = pontos.map((p, i) => {
    const x = (i / (pontos.length - 1)) * 320
    const y = 130 - ((p.bookings - min) / span) * 110 - 8
    return `${x},${y}`
  })
  return (
    <svg className="h-[150px] w-full" viewBox="0 0 320 130" preserveAspectRatio="none">
      <polygon points={`0,130 ${coords.join(' ')} 320,130`} fill="hsl(var(--primary) / 0.12)" />
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={2.5}
      />
    </svg>
  )
}

function LinhaKV({
  icone: Icone,
  rotulo,
  valor,
}: {
  icone: LucideIcon
  rotulo: string
  valor: string
}): ReactElement {
  return (
    <div className="border-border/60 flex items-center gap-4 border-b py-5 last:border-b-0">
      <span className="border-primary/40 text-primary flex h-14 w-14 shrink-0 items-center justify-center rounded-full border">
        <Icone className="h-7 w-7" aria-hidden />
      </span>
      <span className="text-2xl">{rotulo}</span>
      <span className="text-primary ml-auto font-serif text-4xl">{valor}</span>
    </div>
  )
}

function Funil({ estagios }: { estagios: EstagioForecast[] }): ReactElement {
  const max = Math.max(...estagios.map((e) => e.valor), 1)
  return (
    <div className="flex h-full flex-col justify-center gap-4">
      {estagios.map((e) => (
        <div key={e.estagio} className="grid grid-cols-[190px_1fr_auto] items-center gap-3">
          <span className="text-muted-foreground truncate text-xl">{e.estagio}</span>
          <div className="bg-muted h-6 overflow-hidden rounded-full">
            <div
              className="from-primary/70 to-primary h-full rounded-full bg-gradient-to-r"
              style={{ width: `${(e.valor / max) * 100}%` }}
            />
          </div>
          <span className="text-muted-foreground text-xl font-medium tabular-nums">
            {moedaCompacta(e.valor)}
          </span>
        </div>
      ))}
    </div>
  )
}

function PilhaPagamento({ itens }: { itens: { label: string; valor: number }[] }): ReactElement {
  const total = itens.reduce((s, i) => s + i.valor, 0) || 1
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="flex h-12 overflow-hidden rounded-lg">
        {itens.map((item, i) => (
          <div
            key={item.label}
            style={{ width: `${(item.valor / total) * 100}%`, background: corDaSerie(i) }}
          />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
        {itens.map((item, i) => (
          <div key={item.label} className="flex items-center gap-3 text-xl">
            <span
              className="h-4 w-4 shrink-0 rounded-sm"
              style={{ background: corDaSerie(i) }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <span className="text-muted-foreground font-medium tabular-nums">
              {percentual(item.valor / total, 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Ranking({
  itens,
}: {
  itens: { label: string; valor: number; detalhe: string }[]
}): ReactElement {
  const max = Math.max(...itens.map((i) => i.valor), 1)
  return (
    <div className="flex h-full flex-col justify-between gap-3">
      {itens.map((item, i) => (
        <div
          key={item.label}
          className="grid grid-cols-[44px_minmax(0,1.3fr)_2fr_auto] items-center gap-4"
        >
          <span className="text-primary text-center font-serif text-3xl">{i + 1}</span>
          <span className="truncate text-2xl">{item.label}</span>
          <div className="bg-muted h-6 overflow-hidden rounded-full">
            <div
              className="from-primary/70 to-primary h-full rounded-full bg-gradient-to-r"
              style={{ width: `${(item.valor / max) * 100}%` }}
            />
          </div>
          <span className="text-primary min-w-[210px] text-right font-serif text-3xl tabular-nums">
            {moedaCompacta(item.valor)}
            <span className="text-muted-foreground ml-2 font-sans text-base">{item.detalhe}</span>
          </span>
        </div>
      ))}
    </div>
  )
}

function BarrasHorizontais({
  itens,
  vazio,
}: {
  itens: { label: string; valor: number }[]
  vazio: string
}): ReactElement {
  if (itens.length === 0) {
    return <p className="text-muted-foreground text-2xl">{vazio}</p>
  }
  const max = Math.max(...itens.map((i) => i.valor), 1)
  return (
    <ul className="flex h-full flex-col justify-center gap-5">
      {itens.slice(0, 6).map((item, i) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-3 text-2xl">
            <span className="min-w-0 truncate">{item.label}</span>
            <span className="text-muted-foreground shrink-0 font-medium tabular-nums">
              {moedaCompacta(item.valor)}
            </span>
          </div>
          <div className="bg-muted mt-2 h-4 overflow-hidden rounded-full">
            <div
              className="h-full rounded-full"
              style={{ width: `${(item.valor / max) * 100}%`, background: corDaSerie(i) }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

function BarrasSemanais({ semanas }: { semanas: SemanaFechada[] }): ReactElement {
  if (semanas.length === 0) {
    return <p className="text-muted-foreground text-2xl">Sem semanas no período.</p>
  }
  const max = Math.max(...semanas.map((s) => s.receita), 1)
  return (
    <div className="scrollbar-fino flex h-full items-end gap-2 overflow-x-auto pb-1">
      {semanas.map((s) => (
        <div
          key={s.label}
          className="flex h-full min-w-[2.75rem] flex-1 flex-col items-center justify-end gap-2"
        >
          <span className="text-primary rotate-180 text-base font-medium tabular-nums [writing-mode:vertical-rl]">
            {moedaCompacta(s.receita)}
          </span>
          <div
            className="bg-primary w-full rounded-t"
            style={{ height: `${(s.receita / max) * 62}%` }}
          />
          <span className="text-muted-foreground text-base">{s.label}</span>
        </div>
      ))}
    </div>
  )
}

function BarrasCanais({ itens }: { itens: { label: string; valor: number }[] }): ReactElement {
  if (itens.length === 0) {
    return <p className="text-muted-foreground text-2xl">Nenhum lead no período.</p>
  }
  const max = Math.max(...itens.map((i) => i.valor), 1)
  return (
    <ul className="flex h-full flex-col justify-center gap-5">
      {itens.slice(0, 6).map((item, i) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-3 text-3xl">
            <span className="min-w-0 truncate">{item.label}</span>
            <span className="text-muted-foreground shrink-0 font-semibold tabular-nums">
              {inteiro(item.valor)}
            </span>
          </div>
          <div className="bg-muted mt-2 h-5 overflow-hidden rounded-full">
            <div
              className="h-full rounded-full"
              style={{ width: `${(item.valor / max) * 100}%`, background: corDaSerie(i) }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

type TelaComercial = 'resultado' | 'mix' | 'executivos' | 'aquisicao'

// Tabs internas do telão: um único bloco comercial, navegável por aba.
const ABAS_TELA: { tela: TelaComercial; rotulo: string }[] = [
  { tela: 'resultado', rotulo: 'Resultado & metas' },
  { tela: 'mix', rotulo: 'Pipeline & mix' },
  { tela: 'executivos', rotulo: 'Executivos' },
  { tela: 'aquisicao', rotulo: 'Aquisição & funil' },
]

export interface PainelDensoProps {
  relatorio: Relatorio
  serie?: SerieHistorica
  cac?: Cac
  leads?: Leads
}

/**
 * O painel comercial num telão executivo (navy + dourado + serifa, ref. Enjoy
 * Business): KPIs em destaque, metas, ranking, pipeline e mix — desenhado num
 * canvas fixo 16:9 que o PalcoFit escala pra caber sem rolagem.
 */
export function PainelDenso({ relatorio, serie, cac, leads }: PainelDensoProps): ReactElement {
  const [tela, setTela] = useState<TelaComercial>('resultado')
  const {
    panorama,
    forecast,
    forecastPorVendedor,
    porVendedor,
    porProduto,
    porCanal,
    recebimentos,
    faixasDeTicket,
    pendencias,
    vendasDetalhadas,
  } = relatorio
  const progressoAnual = serie?.progressoAnual
  // Só meses com receita: o mês corrente incompleto (zero) sujaria barras e delta.
  const pontos = (serie?.pontos ?? []).filter((p) => p.bookings > 0)
  const metaMensal = pontos.length > 0 ? pontos[pontos.length - 1]!.meta : 0

  const kpis = [
    { rotulo: 'Receita contratada', valor: moedaCompacta(panorama.receitaLiquida) },
    { rotulo: 'Receita bruta', valor: moedaCompacta(panorama.receitaBruta) },
    { rotulo: 'Descontos', valor: moedaCompacta(panorama.desconto) },
    {
      rotulo: 'Desconto médio',
      valor: panorama.nVendas > 0 ? moedaCompacta(panorama.desconto / panorama.nVendas) : '—',
    },
    { rotulo: 'Ticket médio', valor: moedaCompacta(panorama.ticketMedio) },
    {
      rotulo: 'Ciclo médio',
      valor:
        relatorio.cicloMedioDias > 0
          ? `${inteiro(Math.round(relatorio.cicloMedioDias))} dias`
          : '—',
    },
    { rotulo: 'Cortesias', valor: moedaCompacta(panorama.cortesias) },
    {
      rotulo: 'Dados faltando',
      valor: inteiro(pendencias.reduce((total, p) => total + p.negocios, 0)),
    },
  ]

  return (
    // Altura MÍNIMA, não fixa: a tabela de vendas detalhadas cresce o canvas e
    // o PalcoFit re-escala pra caber — com altura fixa ela vazava do slide.
    <div className="flex min-h-[1400px] w-full flex-col gap-3 p-1">
      <div className="flex items-start gap-3">
        <div className="bg-primary h-14 w-2 shrink-0 rounded" aria-hidden />
        <div>
          <h1 className="font-serif text-5xl font-normal leading-none tracking-tight">Vendas</h1>
          <p className="text-muted-foreground mt-2 text-xl">
            Resumo executivo — {relatorio.periodo.label}
          </p>
        </div>
        <span className="text-primary ml-auto self-center font-serif text-3xl tracking-[0.35em]">
          ENJOY BUSINESS
        </span>
      </div>

      <div className="flex shrink-0 gap-3">
        {ABAS_TELA.map((aba) => (
          <button
            key={aba.tela}
            type="button"
            onClick={() => setTela(aba.tela)}
            aria-pressed={tela === aba.tela}
            className={cn(
              'rounded-lg border px-6 py-3 text-xl font-medium transition-colors',
              tela === aba.tela
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {tela === 'resultado' ? (
          <>
            <div className="grid shrink-0 grid-cols-8 gap-3">
            {kpis.map((kpi) => (
              <div
                key={kpi.rotulo}
                className="bg-card/70 border-primary/15 flex flex-col justify-center rounded-xl border px-4 py-4"
              >
                <p className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                  {kpi.rotulo}
                </p>
                <p className="text-primary mt-1 font-serif text-3xl leading-none">{kpi.valor}</p>
              </div>
            ))}
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-[1.5fr_1.3fr_1fr] gap-3">
            <Cartao titulo="Receita contratada" origem="hubspot" campo="deal.amount · closedate">
              <div className="flex h-full items-center gap-6">
                <div>
                  <NumeroGrande>{moedaCompacta(panorama.receitaLiquida)}</NumeroGrande>
                  <Delta valor={variacaoMensal(pontos)} rotulo="vs. mês anterior" />
                </div>
                <div className="min-w-0 flex-1">
                  <BarrasMensais pontos={pontos} meta={metaMensal} />
                </div>
              </div>
            </Cartao>

            <Cartao
              titulo="Forecast ponderado"
              origem="hubspot"
              campo="deal.amount · stage.probability"
            >
              <NumeroGrande>{moedaCompacta(forecast.ponderado)}</NumeroGrande>
              <div className="mt-3">
                <Sparkline pontos={pontos} />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-muted-foreground text-base">Conservador</p>
                  <p className="text-primary font-serif text-3xl">
                    {moedaCompacta(forecast.cenarios.conservador)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-base">Realista</p>
                  <p className="text-primary font-serif text-3xl">
                    {moedaCompacta(forecast.cenarios.realista)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-base">Agressivo</p>
                  <p className="text-primary font-serif text-3xl">
                    {moedaCompacta(forecast.cenarios.agressivo)}
                  </p>
                </div>
              </div>
            </Cartao>

            <Cartao titulo="Resultado do período" origem="hubspot" campo="deal.*">
              <div className="flex h-full flex-col justify-center">
                <LinhaKV icone={Clock} rotulo="Negócios ganhos" valor={inteiro(panorama.nVendas)} />
                <LinhaKV
                  icone={TrendingUp}
                  rotulo="Ticket médio"
                  valor={moedaCompacta(panorama.ticketMedio)}
                />
                <LinhaKV
                  icone={CreditCard}
                  rotulo="Pipeline aberto"
                  valor={moedaCompacta(forecast.potencial)}
                />
              </div>
            </Cartao>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
            <Cartao
              titulo="Metas & projeção do ano"
              origem="metas"
              campo="metas_anuais · deal.amount"
            >
              {progressoAnual ? (
            <div className="flex h-full flex-col gap-2">
              <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
                <div>
                  <p className="text-muted-foreground text-lg">Projeção do ano (run-rate)</p>
                  <NumeroGrande>{moedaCompacta(progressoAnual.runRate)}</NumeroGrande>
                  <p className="text-muted-foreground mt-1.5 text-xl">
                    {moedaCompacta(progressoAnual.acumulado)} em{' '}
                    {inteiro(progressoAnual.mesesDecorridos)} meses
                  </p>
                </div>
                {progressoAnual.niveis.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {progressoAnual.niveis.map((n) => (
                      <span
                        key={n.nivel}
                        className="border-primary/20 flex flex-col rounded-lg border px-4 py-2"
                      >
                        <span className="text-muted-foreground text-base">Meta {n.nivel}</span>
                        <span className="text-primary font-serif text-3xl">
                          {percentual(n.atingimentoProjetado, 0)}
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-2xl">Nenhuma meta anual cadastrada.</p>
                )}
              </div>
              <GraficoSerie pontos={pontos} />
            </div>
          ) : (
            <p className="text-muted-foreground text-2xl">Série anual indisponível.</p>
          )}
            </Cartao>

            <Cartao
              titulo="Ranking de executivos — realizado"
              origem="hubspot"
              campo="deal.amount · closer"
            >
              <Ranking
                itens={porVendedor.slice(0, 5).map((v) => ({
                  label: v.vendedor,
                  valor: v.receitaLiquida,
                  detalhe: `${inteiro(v.negocios)} neg.`,
                }))}
              />
            </Cartao>
          </div>
        </>
      ) : tela === 'mix' ? (
        <>
          <div className="grid min-h-0 flex-1 grid-cols-[1.3fr_1fr] gap-3">
            <Cartao titulo="Pipeline" origem="hubspot" campo="deal.amount · dealstage">
              <div className="flex h-full flex-col gap-4">
                <div className="grid shrink-0 grid-cols-2 gap-3">
                  <div className="border-primary/15 rounded-xl border p-4 text-center">
                    <p className="text-muted-foreground text-lg">Potencial</p>
                    <p className="text-primary font-serif text-4xl">
                      {moedaCompacta(forecast.potencial)}
                    </p>
                  </div>
                  <div className="border-primary/15 rounded-xl border p-4 text-center">
                    <p className="text-muted-foreground text-lg">Negócios</p>
                    <p className="text-primary font-serif text-4xl">{inteiro(forecast.negocios)}</p>
                  </div>
                </div>
                <Funil estagios={forecast.estagios} />
              </div>
            </Cartao>

            <Cartao titulo="Receita por produto" origem="hubspot" campo="line_item.name">
              <BarrasHorizontais
                itens={porProduto.map((p) => ({ label: p.label, valor: p.receita }))}
                vazio="Nenhum produto no período."
              />
            </Cartao>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-3 gap-3">
            <Cartao titulo="Canais de aquisição" origem="hubspot" campo="deal.deal_source">
              <BarrasHorizontais
                itens={porCanal.map((c) => ({ label: c.label, valor: c.receita }))}
                vazio="Nenhum canal no período."
              />
            </Cartao>

            <Cartao titulo="Formas de recebimento" origem="hubspot" campo="deal.meio_de_pagamento">
              <PilhaPagamento itens={recebimentos.map((r) => ({ label: r.meio, valor: r.valor }))} />
            </Cartao>

            <Cartao titulo="Vendas por faixa de valor" origem="hubspot" campo="deal.amount">
              <BarrasHorizontais
                itens={faixasDeTicket.map((f) => ({ label: f.faixa, valor: f.receita }))}
                vazio="Nenhuma venda no período."
              />
            </Cartao>
          </div>
        </>
      ) : tela === 'executivos' ? (
        <>
          <Cartao
            className="flex-1"
            titulo="Vendas por executivo — detalhe"
            origem="hubspot"
            campo="deal.amount · line items"
          >
            {vendasDetalhadas.length === 0 ? (
              <p className="text-muted-foreground text-2xl">Nenhuma venda no período.</p>
            ) : (
              <TabelaExecutivos
                colunas={[
                  { chave: 'vendedor', rotulo: 'Executivo' },
                  { chave: 'negocio', rotulo: 'Negócio' },
                  { chave: 'produtos', rotulo: 'Produto(s)' },
                  { chave: 'valor', rotulo: 'Valor', direita: true },
                  { chave: 'desconto', rotulo: 'Desconto', direita: true },
                  { chave: 'pagamento', rotulo: 'Pagamento' },
                  { chave: 'parcelas', rotulo: 'Parc.', direita: true },
                  { chave: 'fechadoEm', rotulo: 'Fechado em', direita: true },
                ]}
                linhas={vendasDetalhadas.map((v) => ({
                  chave: v.id,
                  celulas: {
                    vendedor: v.vendedor,
                    negocio: v.empresa !== null ? `${v.nome} · ${v.empresa}` : v.nome,
                    produtos: v.produtos.length > 0 ? v.produtos.join(', ') : '—',
                    valor: moedaCompacta(v.valorLiquido),
                    desconto: v.desconto !== null ? moedaCompacta(v.desconto) : '—',
                    pagamento: v.meioPagamento ?? '—',
                    parcelas: v.parcelas > 0 ? inteiro(v.parcelas) : '—',
                    fechadoEm: dataCurta(v.fechadoEm),
                  },
                }))}
              />
            )}
          </Cartao>

          <Cartao
            className="shrink-0"
            titulo="Pipeline forecast por executivo"
            origem="hubspot"
            campo="deal.amount × probabilidade do estágio"
          >
            {forecastPorVendedor.length === 0 ? (
              <p className="text-muted-foreground text-2xl">Nenhum negócio em aberto.</p>
            ) : (
              <TabelaExecutivos
                colunas={[
                  { chave: 'vendedor', rotulo: 'Executivo' },
                  { chave: 'negocios', rotulo: 'Negócios em aberto', direita: true },
                  { chave: 'potencial', rotulo: 'Potencial (100%)', direita: true },
                  { chave: 'ponderado', rotulo: 'Forecast ponderado', direita: true },
                ]}
                linhas={forecastPorVendedor.map((f) => ({
                  chave: f.vendedor,
                  celulas: {
                    vendedor: f.vendedor,
                    negocios: inteiro(f.negocios),
                    potencial: moedaCompacta(f.potencial),
                    ponderado: moedaCompacta(f.ponderado),
                  },
                }))}
              />
            )}
          </Cartao>
        </>
      ) : (
        <>
          <Cartao
            className="min-h-0 flex-1"
            titulo="Receita fechada — semana a semana"
            origem="hubspot"
            campo="deal.amount · closedate"
          >
            <BarrasSemanais semanas={relatorio.semanas} />
          </Cartao>

          <div className="grid min-h-0 flex-1 grid-cols-[1fr_1.6fr_1fr] gap-3">
            <Cartao titulo="Conversão MQL → cliente" origem="hubspot" campo="lifecyclestage">
              <div className="flex h-full flex-col justify-center gap-3">
                {leads && leads.conversaoMql.taxa !== null ? (
                  <>
                    <NumeroGrande>{percentual(leads.conversaoMql.taxa, 0)}</NumeroGrande>
                    <p className="text-muted-foreground text-2xl">
                      {inteiro(leads.conversaoMql.clientes)} de {inteiro(leads.conversaoMql.mqls)}{' '}
                      MQL viraram cliente
                    </p>
                  </>
                ) : (
                  <>
                    <NumeroGrande>—</NumeroGrande>
                    <p className="text-muted-foreground text-2xl">Sem MQL no período.</p>
                  </>
                )}
              </div>
            </Cartao>

            <Cartao titulo="Leads por canal" origem="hubspot" campo="lead_source">
              <BarrasCanais
                itens={(leads?.porCanal ?? []).map((c) => ({ label: c.canal, valor: c.total }))}
              />
            </Cartao>

            <Cartao
              titulo="Custo de aquisição (CAC)"
              origem="contaazul"
              campo="centro de custo de marketing"
            >
              <div className="flex h-full flex-col justify-center gap-3">
                {!cac?.configurado ? (
                  <>
                    <NumeroGrande>—</NumeroGrande>
                    <p className="text-muted-foreground text-2xl">
                      Aguardando a configuração dos centros de marketing no Conta Azul.
                    </p>
                  </>
                ) : cac.cac === null ? (
                  <>
                    <NumeroGrande>—</NumeroGrande>
                    <p className="text-muted-foreground text-2xl">Sem novos clientes no período.</p>
                  </>
                ) : (
                  <>
                    <NumeroGrande>{moedaCompacta(cac.cac)}</NumeroGrande>
                    <p className="text-muted-foreground text-2xl">
                      {moedaCompacta(cac.investimento)} ÷ {inteiro(cac.novosClientes)} novo(s)
                      cliente(s)
                    </p>
                  </>
                )}
              </div>
            </Cartao>
          </div>
        </>
      )}
      </div>
    </div>
  )
}
